import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import StatTable from "./StatTable";
import TrustStrip from "./TrustStrip";
import { rate3, rate2, rate1, pct, cnt, velo, inningsFromOuts, EMDASH } from "./statFormat";
import { BarChart3 } from "lucide-react";

const SCOPES = [
  ["career", "Career"], ["last5", "Last 5"], ["last10", "Last 10"], ["last30", "Last 30 Days"],
];

// ---- column builders -------------------------------------------------------
const H_COLS = [
  { key: "label", label: "Split", better: null, sticky: true, fmt: (r) => r.label },
  { key: "g", label: "G", better: "high", fmt: (r) => cnt(r.g) },
  { key: "pa", label: "PA", better: "high", fmt: (r) => cnt(r.pa) },
  { key: "ab", label: "AB", better: "high", fmt: (r) => cnt(r.ab) },
  { key: "r", label: "R", better: "high", fmt: (r) => cnt(r.r) },
  { key: "h", label: "H", better: "high", fmt: (r) => cnt(r.h) },
  { key: "doubles", label: "2B", better: "high", fmt: (r) => cnt(r.doubles) },
  { key: "triples", label: "3B", better: "high", fmt: (r) => cnt(r.triples) },
  { key: "hr", label: "HR", better: "high", fmt: (r) => cnt(r.hr) },
  { key: "rbi", label: "RBI", better: "high", fmt: (r) => cnt(r.rbi) },
  { key: "bb", label: "BB", better: "high", fmt: (r) => cnt(r.bb) },
  { key: "so", label: "SO", better: "low", fmt: (r) => cnt(r.so) },
  { key: "hbp", label: "HBP", better: "high", fmt: (r) => cnt(r.hbp) },
  { key: "sb", label: "SB", better: "high", fmt: (r) => cnt(r.sb) },
  { key: "cs", label: "CS", better: "low", fmt: (r) => cnt(r.cs) },
  { key: "avg", label: "AVG", better: "high", fmt: (r) => rate3(r.avg) },
  { key: "obp", label: "OBP", better: "high", fmt: (r) => rate3(r.obp) },
  { key: "slg", label: "SLG", better: "high", fmt: (r) => rate3(r.slg) },
  { key: "ops", label: "OPS", better: "high", fmt: (r) => rate3(r.ops) },
  { key: "bb_pct", label: "BB%", better: "high", fmt: (r) => pct(r.bb_pct) },
  { key: "k_pct", label: "K%", better: "low", fmt: (r) => pct(r.k_pct) },
  { key: "qab_pct", label: "QAB%", better: "high", fmt: (r) => pct(r.qab_pct) },
  { key: "iso", label: "ISO", better: "high", fmt: (r) => rate3(r.iso) },
  { key: "babip", label: "BABIP", better: "high", fmt: (r) => rate3(r.babip) },
];

const P_COLS = [
  { key: "label", label: "Split", better: null, sticky: true, fmt: (r) => r.label },
  { key: "g", label: "G", better: "high", fmt: (r) => cnt(r.g) },
  { key: "gs", label: "GS", better: "high", fmt: (r) => cnt(r.gs) },
  { key: "w", label: "W", better: "high", fmt: (r) => cnt(r.w) },
  { key: "l", label: "L", better: "low", fmt: (r) => cnt(r.l) },
  { key: "sv", label: "SV", better: "high", fmt: (r) => cnt(r.sv) },
  { key: "ip_outs", label: "IP", better: "high", fmt: (r) => r.ip, sortValue: (r) => r.ip_outs },
  { key: "h", label: "H", better: "low", fmt: (r) => cnt(r.h) },
  { key: "r", label: "R", better: "low", fmt: (r) => cnt(r.r) },
  { key: "er", label: "ER", better: "low", fmt: (r) => cnt(r.er) },
  { key: "bb", label: "BB", better: "low", fmt: (r) => cnt(r.bb) },
  { key: "so", label: "SO", better: "high", fmt: (r) => cnt(r.so) },
  { key: "era", label: "ERA", better: "low", fmt: (r) => rate2(r.era) },
  { key: "whip", label: "WHIP", better: "low", fmt: (r) => rate2(r.whip) },
  { key: "k9", label: "K/9", better: "high", fmt: (r) => rate1(r.k9) },
  { key: "bb9", label: "BB/9", better: "low", fmt: (r) => rate1(r.bb9) },
  { key: "h9", label: "H/9", better: "low", fmt: (r) => rate1(r.h9) },
  { key: "hr9", label: "HR/9", better: "low", fmt: (r) => rate1(r.hr9) },
  { key: "kbb", label: "K/BB", better: "high", fmt: (r) => rate2(r.kbb) },
  { key: "strike_pct", label: "Strike%", better: "high", fmt: (r) => pct(r.strike_pct) },
  { key: "opp_avg", label: "Opp AVG", better: "low", fmt: (r) => rate3(r.opp_avg) },
  { key: "max_velo", label: "Max Velo", better: "high", fmt: (r) => velo(r.max_velo) },
];

const F_COLS = [
  { key: "position", label: "Pos", better: null, sticky: true, fmt: (r) => r.position },
  { key: "inn", label: "INN", better: "high", fmt: (r) => inningsFromOuts(r.def_outs), sortValue: (r) => r.def_outs },
  { key: "po", label: "PO", better: "high", fmt: (r) => cnt(r.po) },
  { key: "a", label: "A", better: "high", fmt: (r) => cnt(r.a) },
  { key: "e", label: "E", better: "low", fmt: (r) => cnt(r.e) },
  { key: "total_chances", label: "TC", better: "high", fmt: (r) => cnt(r.total_chances) },
  { key: "fielding_pct", label: "FLD%", better: "high", fmt: (r) => rate3(r.fielding_pct) },
  { key: "dp", label: "DP", better: "high", fmt: (r) => cnt(r.dp) },
  { key: "of_assists", label: "OF A", better: "high", fmt: (r) => cnt(r.of_assists) },
  { key: "pb", label: "PB", better: "low", fmt: (r) => cnt(r.pb) },
  { key: "sb_allowed", label: "SB", better: "low", fmt: (r) => cnt(r.sb_allowed) },
  { key: "cs", label: "CS", better: "high", fmt: (r) => cnt(r.cs) },
  { key: "cs_pct", label: "CS%", better: "high", fmt: (r) => pct(r.cs_pct) },
];

function hitRow(label, t, r, key) {
  return { _key: key, label, g: undefined, ...t, ...r };
}

const Empty = ({ children }) => (
  <div className="border border-dashed border-slate-300 rounded-sm p-6 text-center text-sm text-slate-500" data-testid="stats-empty">
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h4 className="font-display text-lg uppercase tracking-tight text-[#0A192F] mt-8 mb-3 first:mt-0">{children}</h4>
);

export default function PlayerStatisticsTab({ playerId, filters }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [splits, setSplits] = useState([]);
  const [loading, setLoading] = useState(true);

  const scope = params.get("sscope") || "career";
  const tourn = params.get("stourn") || "";
  const team = params.get("steam") || "";
  const ctx = params.get("scontext") || "";

  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  };

  useEffect(() => {
    if (!filters?.has_approved_data) { setLoading(false); return; }
    setLoading(true);
    const q = { scope };
    if (tourn) q.tournament_id = tourn;
    if (team) q.team_id = team;
    if (ctx) q.context = ctx;
    api.get(`/players/${playerId}/game-stats`, { params: q })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [playerId, scope, tourn, team, ctx, filters]);

  useEffect(() => {
    if (!filters?.has_approved_data) return;
    api.get(`/players/${playerId}/tournament-summaries`).then((r) => setSplits(r.data?.items || [])).catch(() => setSplits([]));
  }, [playerId, filters]);

  if (loading) return <div className="label-overline text-slate-400 py-10">Loading statistics…</div>;

  if (!filters?.has_approved_data) {
    return (
      <div data-testid="statistics-tab">
        <Empty>
          <BarChart3 className="w-6 h-6 mx-auto text-slate-300 mb-2" />
          No approved game statistics yet. Verified game data will appear here once CBR approves this player's box scores.
        </Empty>
      </div>
    );
  }

  const agg = data?.aggregate;
  const trust = data?.trust;
  const s = agg?.sample || {};
  const hasHit = agg && agg.games_hit > 0;
  const hasPitch = agg && agg.games_pitched > 0;
  const fieldRows = (agg?.fielding?.by_position || []).map((f) => ({ ...f, _key: f.position }));

  const sampleTiles = [
    ["Games", agg?.games ?? 0],
    ["PA", s.pa ?? 0],
    ["AB", s.ab ?? 0],
    ["IP", s.ip ?? "0.0"],
    ["Fielding Chances", s.fielding_chances ?? 0],
    ["Opponents", trust?.opponents ?? 0],
    ["Tournaments", trust?.tournaments ?? 0],
  ];

  const hittingSummaryRows = hasHit
    ? [{ _key: "total", label: SCOPES.find((x) => x[0] === scope)?.[1] || "Career", g: agg.games_hit, ...agg.hitting.totals, ...agg.hitting.rates }]
    : [];
  const pitchingSummaryRows = hasPitch
    ? [{ _key: "total", label: SCOPES.find((x) => x[0] === scope)?.[1] || "Career", g: agg.games_pitched, ...agg.pitching.totals, ...agg.pitching.rates }]
    : [];

  const hitSplitRows = splits.filter((t) => cnt(t.aggregate?.hitting?.totals?.pa) > 0).map((t) => ({
    _key: t.tournament_id, label: t.name, g: t.aggregate.games_hit,
    ...t.aggregate.hitting.totals, ...t.aggregate.hitting.rates,
  }));

  return (
    <div data-testid="statistics-tab" className="space-y-2">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1" role="group" aria-label="Statistics scope">
          {SCOPES.map(([k, label]) => (
            <button
              key={k}
              data-testid={`stats-scope-${k}`}
              onClick={() => setFilter("sscope", k === "career" ? "" : k)}
              aria-pressed={scope === k}
              className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border transition-colors focus-visible:ring-2 focus-visible:ring-[#E63946] focus-visible:outline-none ${scope === k ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-600 border-slate-300 hover:border-[#0A192F]"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <select data-testid="stats-filter-context" aria-label="Filter by team context" value={ctx}
          onChange={(e) => setFilter("scontext", e.target.value)}
          className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 bg-white">
          <option value="">All Teams</option>
          <option value="cbr_nation">CBR Nation</option>
          <option value="regular_team">Regular Team</option>
        </select>
        {filters.tournaments?.length > 1 && (
          <select
            data-testid="stats-filter-tournament"
            aria-label="Filter by tournament"
            value={tourn}
            onChange={(e) => setFilter("stourn", e.target.value)}
            className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 bg-white"
          >
            <option value="">All Tournaments</option>
            {filters.tournaments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        {filters.teams?.length > 1 && (
          <select
            data-testid="stats-filter-team"
            aria-label="Filter by team"
            value={team}
            onChange={(e) => setFilter("steam", e.target.value)}
            className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 bg-white"
          >
            <option value="">All Teams</option>
            {filters.teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </div>

      {/* Sample size */}
      <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 mt-4" data-testid="stats-sample">
        {sampleTiles.map(([label, val]) => (
          <div key={label} className="border border-slate-200 rounded-sm px-2 py-2 text-center">
            <div className="font-data text-xl text-[#0A192F] tabular-nums">{val}</div>
            <div className="text-[9px] uppercase tracking-wider text-slate-400 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Quality At-Bat highlight */}
      {hasHit && cnt(agg.hitting.totals.qab) > 0 && (
        <div data-testid="qab-card" className="mt-4 bg-[#0A192F] text-white rounded-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
          <div className="flex items-baseline gap-3">
            <span className="font-data text-4xl sm:text-5xl tabular-nums text-[#E63946] leading-none" data-testid="qab-pct-value">{pct(agg.hitting.rates.qab_pct)}</span>
            <div>
              <div className="label-overline text-white/50 text-[10px]">Quality At-Bat %</div>
              <div className="text-xs text-white/70 mt-0.5" data-testid="qab-count">{cnt(agg.hitting.totals.qab)} QAB · {cnt(s.pa)} PA</div>
            </div>
          </div>
          <p className="text-xs text-white/60 sm:max-w-sm">How often this hitter produces a competitive, productive plate appearance — a key plate-approach metric for recruiters. QAB% = QAB ÷ PA.</p>
        </div>
      )}

      {agg?.games === 0 ? (
        <Empty>Not enough data for this filter. Try a wider range.</Empty>
      ) : (
        <>
          <SectionTitle>Hitting</SectionTitle>
          {hasHit ? (
            <StatTable testid="hitting-summary" columns={H_COLS} rows={hittingSummaryRows} sortable={false} caption="Hitting summary" />
          ) : (
            <Empty>No plate appearances recorded for this filter.</Empty>
          )}

          <SectionTitle>Pitching</SectionTitle>
          {hasPitch ? (
            <StatTable testid="pitching-summary" columns={P_COLS} rows={pitchingSummaryRows} sortable={false} caption="Pitching summary" />
          ) : (
            <Empty>No pitching appearances recorded.</Empty>
          )}

          <SectionTitle>Fielding</SectionTitle>
          {fieldRows.length ? (
            <StatTable testid="fielding-summary" columns={F_COLS} rows={fieldRows} initialSort="inn" caption="Fielding summary by position" />
          ) : (
            <Empty>No fielding chances recorded.</Empty>
          )}

          {hitSplitRows.length > 1 && (
            <>
              <SectionTitle>Hitting by Tournament</SectionTitle>
              <StatTable testid="hitting-splits" columns={H_COLS} rows={hitSplitRows} initialSort="ops" caption="Hitting splits by tournament — click a header to sort" />
              <p className="text-[11px] text-slate-400 mt-1">Tap any column header to sort splits.</p>
            </>
          )}

          <TrustStrip trust={trust} className="mt-6" />
        </>
      )}
    </div>
  );
}
